/**
 * The route test's Formula and Rules on their own tabs (moved out of
 * RouteTestStep, 21/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Scale, Sigma } from 'lucide-react';
import { routeTestSource, type DropGenGatewayRanking } from '@trafficpulse/shared';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/layout/Tabs';

import { HourFormulaCard } from '../../general/hour/formula/HourFormulaCard';
import { RulesCard } from '../../rules/RulesCard';
import { routeTestMetricCatalog } from '../route-test-metric-catalog';

type Props = { ranking: DropGenGatewayRanking; onChange: (next: DropGenGatewayRanking) => void };

const TRIGGER = 'h-7 gap-1 px-2.5 py-0 text-[11px] data-[state=active]:bg-background data-[state=active]:shadow-sm';

const FormulaBlock: FC<Props> = ({ ranking, onChange }) => (
  <div className="flex flex-col gap-2">
    <p className="text-xs text-muted-foreground">How each gateway scores. Today only.</p>
    <HourFormulaCard formula={ranking.formula} bare dateLocked metrics={routeTestMetricCatalog(routeTestSource(ranking))} onChange={(formula) => onChange({ ...ranking, formula })} />
  </div>
);

const RulesBlock: FC<Props> = ({ ranking, onChange }) => (
  <div className="flex flex-col gap-2">
    <p className="text-xs text-muted-foreground">
      What those scores may change. No ‘Use for the rest of today’ rule: the best-scored gateway wins. Add one to require a bar first
      (for example Delivered rate above 80 %). The winner and its best sender go onto the other containers’ unsent drops.
    </p>
    <RulesCard
      surface="routeTest"
      bare
      rules={ranking.rules ?? []}
      metrics={routeTestMetricCatalog(routeTestSource(ranking))}
      onChange={(rules) => onChange({ ...ranking, rules })}
      firstRange={ranking.formula.groups[0]}
    />
  </div>
);

export const RankingTabs: FC<Props> = ({ ranking, onChange }) => (
  <Tabs defaultValue="formula" className="flex flex-col gap-3">
    <TabsList aria-label="Route test views" className="h-8 w-fit justify-start gap-0.5 p-0.5">
      <TabsTrigger value="formula" className={TRIGGER}>
        <Sigma className="h-3 w-3" aria-hidden /> Formula
      </TabsTrigger>
      <TabsTrigger value="rules" className={TRIGGER}>
        <Scale className="h-3 w-3" aria-hidden /> Rules
      </TabsTrigger>
    </TabsList>
    <TabsContent value="formula" className="mt-0 focus-visible:outline-none">
      <FormulaBlock ranking={ranking} onChange={onChange} />
    </TabsContent>
    <TabsContent value="rules" className="mt-0 focus-visible:outline-none">
      <RulesBlock ranking={ranking} onChange={onChange} />
    </TabsContent>
  </Tabs>
);
